import React, { useState, useEffect } from 'react';
import { createItem, updateItem, getCoinValue, analyzeCoin, rotateImage, autoCropItemImages, searchNumista, getNumistaDetails, addToNumistaCollection, getNumistaCoinIssues } from '../services/api';
import HoverImage from './HoverImage';
import FullscreenImageViewer from './FullscreenImageViewer';
import { Search, RotateCw, Upload, Sparkles, X, Check, ExternalLink } from 'lucide-react';

const ItemForm = ({ item, onClose, onNotification, themeClasses, isDarkMode }) => {
  const [formData, setFormData] = useState({
    type: 'coin',
    country: '',
    year: '',
    denomination: '',
    currency: '',
    material: '',
    rarity: '',
    condition: '',
    estimated_value: '',
    quantity: 1,
    description: '',
    notes: ''
  });
  
  const [imageFrontFile, setImageFrontFile] = useState(null);
  const [imageBackFile, setImageBackFile] = useState(null);
  const [imageFrontPreview, setImageFrontPreview] = useState(null);
  const [imageBackPreview, setImageBackPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [valueInfo, setValueInfo] = useState(null);
  
  // Numista Search State
  const [showNumistaModal, setShowNumistaModal] = useState(false);
  const [numistaQuery, setNumistaQuery] = useState('');
  const [numistaResults, setNumistaResults] = useState([]);
  const [searchingNumista, setSearchingNumista] = useState(false);
  const [numistaId, setNumistaId] = useState(null);
  const [numistaImageFailures, setNumistaImageFailures] = useState({});
  
  // Variety/Issue Selection State
  const [showVarietyModal, setShowVarietyModal] = useState(false);
  const [varieties, setVarieties] = useState([]);
  const [selectedVariety, setSelectedVariety] = useState('');
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [addingToNumista, setAddingToNumista] = useState(false);
  const [numistaCollectionId, setNumistaCollectionId] = useState(null);
  const [autoCropping, setAutoCropping] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        type: item.type || 'coin',
        country: item.country || '',
        year: item.year || '',
        denomination: item.denomination || '',
        currency: item.currency || '',
        material: item.material || '',
        rarity: item.rarity || 'common',
        condition: item.condition || '',
        estimated_value: item.estimated_value || '',
        quantity: item.quantity || 1,
        description: item.description || '',
        notes: item.notes || ''
      });
      
      // Prefer persisted Numista links from DB fields, fallback to notes parsing.
      if (item.numista_coin_id) {
          setNumistaId(String(item.numista_coin_id));
      } else if (item.notes) {
          const match = item.notes.match(/pieces(\d+)\.html/);
          if (match && match[1]) {
              setNumistaId(match[1]);
          }
      }

      if (item.numista_collection_id) {
          setNumistaCollectionId(String(item.numista_collection_id));
      } else {
          setNumistaCollectionId(null);
      }

      // Use relative paths to allow proxy to handle backend connection
      if (item.image_front_path) {
        setImageFrontPreview(`/api/uploads/${item.image_front_path}`);
      } else if (item.image_path) {
        setImageFrontPreview(`/api/uploads/${item.image_path}`);
      }

      if (item.image_back_path) {
        setImageBackPreview(`/api/uploads/${item.image_back_path}`);
      }
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e, side) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (side === 'front') {
          setImageFrontFile(file);
          setImageFrontPreview(reader.result);
        } else {
          setImageBackFile(file);
          setImageBackPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRotate = async (side) => {
      if (!item || !item.id) {
          if (onNotification) onNotification("Save the item first before rotating.", "warning");
          return;
      }
      
      try {
          const res = await rotateImage(item.id, side, 90);
          if (onNotification) onNotification("Image rotated!", "success");
          
          // Force refresh image by appending timestamp
          const timestamp = new Date().getTime();
            if (side === 'front') {
              const currentPath = item.image_front_path || item.image_path;
              if (currentPath) {
                setImageFrontPreview(`/api/uploads/${currentPath}?t=${timestamp}`);
              } else {
                  // Fallback if path is missing but we rotated (rare)
                   if (onNotification) onNotification("Rotated, but image path missing - reload to see changes.", "info");
              }
          } else {
           const currentPath = item.image_back_path;
           if (currentPath) {
            setImageBackPreview(`/api/uploads/${currentPath}?t=${timestamp}`);
               }
          }
      } catch (err) {
          console.error(err);
          if (onNotification) onNotification("Rotation failed: " + (err.error || err.message), "error");
      }
  };

  const handleAutoCrop = async (side = 'both') => {
    if (!item || !item.id) {
      if (onNotification) onNotification('Save the item first before auto-cropping.', 'warning');
      return;
    }

    try {
      setAutoCropping(true);
      const response = await autoCropItemImages(item.id, side);
      const timestamp = response?.timestamp || Date.now();

      if ((side === 'front' || side === 'both') && (item.image_front_path || item.image_path)) {
        const currentFront = item.image_front_path || item.image_path;
        setImageFrontPreview(`/api/uploads/${currentFront}?t=${timestamp}`);
      }
      if ((side === 'back' || side === 'both') && item.image_back_path) {
        setImageBackPreview(`/api/uploads/${item.image_back_path}?t=${timestamp}`);
      }

      const parts = [];
      if (side === 'front' || side === 'both') {
        parts.push(response?.result?.front?.cropped ? 'front OK' : 'front no-crop');
      }
      if (side === 'back' || side === 'both') {
        parts.push(response?.result?.back?.cropped ? 'back OK' : 'back no-crop');
      }
      if (onNotification) onNotification(`Auto-crop completed (${parts.join(', ')})`, 'success');
    } catch (err) {
      console.error(err);
      if (onNotification) onNotification('Auto-crop failed: ' + (err.error || err.message), 'error');
    } finally {
      setAutoCropping(false);
    }
  };

  const openNumistaSearch = () => {
    // Construct query from form data
    const parts = [];
    if (formData.country) parts.push(formData.country);
    if (formData.denomination) parts.push(formData.denomination);
    if (formData.year) parts.push(formData.year);
    if (formData.currency) parts.push(formData.currency);

    const query = parts.join(' ').trim();
    setNumistaQuery(query);
    setShowNumistaModal(true);
    
    // Auto-trigger search if we have a decent query
    if (query.length > 3) {
        // Use a timeout to allow state to settle or simple direct call logic
        // But since handleNumistaSearch uses state `numistaQuery`, we must wait or pass param.
        // Let's modify handleNumistaSearch to accept optional query override
        performNumistaSearch(query);
    }
  };

  const performNumistaSearch = async (queryOverride) => {
    const q = queryOverride || numistaQuery;
    if (!q) return;

    try {
        setSearchingNumista(true);
      setNumistaImageFailures({});
        const data = await searchNumista(q);
        setNumistaResults(data.coins || []);
    } catch(err) {
        console.error(err);
        if (onNotification) onNotification("Numista search failed", "error");
    } finally {
        setSearchingNumista(false);
    }
  };
    
  // Keep original handle for the button inside modal
  const handleNumistaSearch = () => performNumistaSearch();

  const getNumistaImageCandidates = (coin) => {
    const issuerCode = coin?.issuer?.code || coin?.country?.code;
    const candidates = [
      coin?.thumbnail,
      coin?.picture,
      coin?.image,
      coin?.obverse?.thumbnail,
      coin?.obverse?.picture,
      coin?.reverse?.thumbnail,
      coin?.reverse?.picture,
      issuerCode && coin?.id ? `https://en.numista.com/catalogue/photos/${issuerCode}/${coin.id}-180.jpg` : null,
      issuerCode && coin?.id ? `https://en.numista.com/catalogue/photos/${issuerCode}/${coin.id}-original.jpg` : null
    ].filter(Boolean);

    const normalize = (src) => {
      if (typeof src !== 'string') return null;
      // Prefer 180px variant over original because Numista may deny direct original fetches.
      return src.replace(/-original\.jpg$/i, '-180.jpg');
    };

    return Array.from(new Set(candidates.map(normalize).filter(Boolean)));
  };

  const getNumistaImageSrc = (coin) => {
    if (!coin?.id) return null;
    const failedCount = numistaImageFailures[coin.id] || 0;
    const candidates = getNumistaImageCandidates(coin);
    const target = candidates[failedCount] || null;
    if (!target) return null;
    return `/api/numista/image?url=${encodeURIComponent(target)}`;
  };

  const handleNumistaImageError = (coinId) => {
    if (!coinId) return;
    setNumistaImageFailures(prev => ({
      ...prev,
      [coinId]: (prev[coinId] || 0) + 1
    }));
  };

  const NUMISTA_SECTION_HEADER = 'Numista Details:';

  const removeNumistaSection = (text = '') => {
    const source = String(text || '');
    return source.replace(/\n*Numista Details:\n[\s\S]*$/g, '').replace(/\n{3,}/g, '\n\n').trim();
  };

  const buildNumistaSection = (title, techSpecs) => {
    const lines = [];
    if (title) lines.push(`Title: ${title}`);
    if (techSpecs) lines.push(`Specs: ${techSpecs}`);
    if (lines.length === 0) return '';
    return `${NUMISTA_SECTION_HEADER}\n${lines.join('\n')}`;
  };

  const mergeDescriptionWithNumista = (currentDescription, title, techSpecs) => {
    const base = removeNumistaSection(currentDescription);
    const numistaSection = buildNumistaSection(title, techSpecs);
    if (!numistaSection) return base;
    return base ? `${base}\n\n${numistaSection}` : numistaSection;
  };

  const selectNumistaCoin = async (coin) => {
      // Basic info first
      setFormData(prev => ({
          ...prev,
          description: mergeDescriptionWithNumista(prev.description, coin.title, ''),
          country: coin.country?.name || prev.country,
          year: coin.min_year ? coin.min_year.toString() : prev.year,
          type: 'coin'
      }));
      setShowNumistaModal(false);
      
      // Fetch Details
      if (coin.id) {
          setNumistaId(coin.id);
          try {
              if (onNotification) onNotification("Fetching details from Numista...", "info");
              const details = await getNumistaDetails(coin.id);
              
              // Map Rarity Index (0-100)
              let rarityText = '';
              if (details.rarity_index !== undefined) {
                  const ri = details.rarity_index;
                  if (ri < 10) rarityText = 'common';
                  else if (ri < 30) rarityText = 'uncommon';
                  else if (ri < 60) rarityText = 'rare';
                  else if (ri < 85) rarityText = 'very_rare';
                  else rarityText = 'extremely_rare';
              }

              // Process composition for material
              const material = details.composition?.text || details.composition || '';

              // Append tech specs to description
              const techSpecs = [
                  details.weight ? `Weight: ${details.weight}g` : null,
                  details.diameter ? `Diameter: ${details.diameter}mm` : null,
                  details.thickness ? `Thickness: ${details.thickness}mm` : null,
                  details.shape ? `Shape: ${details.shape}` : null
              ].filter(Boolean).join(' | ');

              const numistaLink = `Link: https://en.numista.com/catalogue/pieces${coin.id}.html`;

              setFormData(prev => ({
                  ...prev,
                  material: material || prev.material,
                  rarity: rarityText || prev.rarity,
                  description: mergeDescriptionWithNumista(prev.description, coin.title, techSpecs),
                  notes: prev.notes ? `${prev.notes}\n${numistaLink}` : numistaLink
              }));

              if (onNotification) onNotification("Details updated from Numista!", "success");

          } catch (e) {
              console.error("Failed to get Numista details", e);
              if (onNotification) onNotification("Could not fetch full details.", "warning");
          }
      }
  };

  const fetchVarietiesAndOpenModal = async () => {
      if (!numistaId) return;
      
      try {
          setLoadingVarieties(true);
          const issues = await getNumistaCoinIssues(numistaId);
          
          // Filter issues if year is set, or show all? 
          // Usually better to show all matching the year, but user might want to see others
          // Let's filter by year if formData.year is present to reduce clutter, BUT provide a "Show all" option?
          // For now, simple sort
          const sorted = issues.sort((a, b) => (a.year || 0) - (b.year || 0));
          
          setVarieties(sorted);
          setShowVarietyModal(true);
          
          // Pre-select if only one option for this year
          if (formData.year) {
              const yearMatches = sorted.filter(v => v.year == formData.year);
               // Don't auto select to force user to verify
          }
          
      } catch (e) {
          console.error("Failed to fetch varieties", e);
          if (onNotification) onNotification("Could not fetch variety list", "warning");
          // Fallback to direct add if varieties fail?
          // Or just show empty modal?
      } finally {
          setLoadingVarieties(false);
      }
  };

  const confirmAddToCollection = async () => {
        if (!numistaId) {
            onNotification && onNotification("Select a Numista coin first", "warning");
            return;
        }

        try {
            setAddingToNumista(true);
            const response = await addToNumistaCollection({
                numistaId,
                year: formData.year,
                condition: formData.condition || 'VF',
                comment: 'Added via MoneyCollector App',
                itemId: item ? item.id : null,
                issueId: selectedVariety || null
            });

            const collectionId = response?.collectionId || response?.data?.id || null;
            if (collectionId) {
                setNumistaCollectionId(String(collectionId));
            }

            setShowVarietyModal(false);
            const actionLabel = response?.action === 'updated' ? 'updated' : 'added';
            onNotification && onNotification(`Coin ${actionLabel} in Numista collection`, "success");

            if (item?.id && response?.localLinked === false) {
              onNotification && onNotification("Added on Numista, but local link failed. Click Update Entry to retry local save.", "warning");
            }

            // Auto-save Numista fields to the local item so the link is never lost.
            if (item?.id) {
                try {
                    const patch = new FormData();
                    if (collectionId) patch.append('numista_collection_id', String(collectionId));
                    if (numistaId) patch.append('numista_coin_id', String(numistaId));
                    await updateItem(item.id, patch);
                    onNotification && onNotification("Item saved with Numista link", "info");
                } catch (saveErr) {
                    console.warn('Auto-save Numista link failed, please save manually', saveErr);
                    onNotification && onNotification("Numista linked but auto-save failed — click Update Entry to persist", "warning");
                }
            } else {
                onNotification && onNotification("Save the item to persist the Numista link", "info");
            }
        } catch (e) {
            console.error(e);
            if (e.code === 'AUTH_REQUIRED' && e.authUrl) {
                if (window.confirm("Authorization Required. Proceed to login?")) {
                    window.location.href = e.authUrl;
                    return;
                }
            }
            onNotification && onNotification("Failed to add: " + (e.error || e.message), "error");
        } finally {
            setAddingToNumista(false);
        }
  };


  const handleAIIdentify = async () => {
    // If we have files, uploads them. If not, check if we have existing paths (from item prop)
    const hasFiles = imageFrontFile || imageBackFile;
    const hasExisting = item && (item.image_front_path || item.image_path || item.image_back_path);
    
    if (!hasFiles && !hasExisting) {
      if (onNotification) onNotification("Please upload an image or have an existing image to use AI Identification.", "error");
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      if (onNotification) onNotification("AI analysis started...", "info");
      
      const formData = new FormData();
      if (imageFrontFile) {
          formData.append('imageFront', imageFrontFile);
      } else if (item && (item.image_front_path || item.image_path)) {
          // Send existing path if no new file
          formData.append('imageFrontPath', item.image_front_path || item.image_path);
      }

      if (imageBackFile) {
          formData.append('imageBack', imageBackFile);
      } else if (item && item.image_back_path) {
          formData.append('imageBackPath', item.image_back_path);
      }
      
      const data = await analyzeCoin(formData);
      
      let estimatedVal = null;
      if (data.estimated_value_min !== undefined && data.estimated_value_min !== null && 
          data.estimated_value_max !== undefined && data.estimated_value_max !== null) {
          estimatedVal = ((data.estimated_value_min + data.estimated_value_max) / 2).toFixed(2);
      }

      setFormData(prev => ({
        ...prev,
        country: data.country || prev.country,
        year: data.year || prev.year,
        denomination: data.denomination || prev.denomination,
        currency: data.currency || prev.currency,
        material: data.material || prev.material,
        rarity: data.rarity || prev.rarity,
        condition: data.condition || prev.condition,
        estimated_value: estimatedVal || prev.estimated_value,
        description: prev.description ? prev.description : (data.raw_text ? `Detected Text:\n${data.raw_text}` : '')
      }));

       if (data.country || data.year) {
           if (onNotification) {
             onNotification(`AI Found: ${data.country || '?'} - ${data.year || '?'}`, "success");
           }
       } else {
           if (onNotification) {
             onNotification("AI analysis completed but confidence was low. Check description.", "warning");
           }
       }

    } catch (err) {
        console.error('AI Analysis Error:', err);
        let errorMessage = "AI Analysis failed. Please try again.";
        
        if (typeof err === 'string') {
            errorMessage = err;
        } else if (err.error) {
            errorMessage = err.error;
        } else if (err.message) {
            errorMessage = err.message;
        }
        
        setError(errorMessage);
        if (onNotification) onNotification(errorMessage, "error");
    } finally {
        setLoading(false);
    }
  };


  const handleFetchValue = async () => {
    if (!formData.country || !formData.year) {
      if (onNotification) onNotification('Please enter country and year first', "warning");
      return;
    }

    try {
      setLoading(true);
      const data = await getCoinValue(formData.country, formData.year, formData.denomination);
      setValueInfo(data);
      
      if (data.estimated_value) {
        const avgValue = (data.estimated_value.min + data.estimated_value.max) / 2;
        setFormData(prev => ({ 
          ...prev, 
          estimated_value: avgValue.toFixed(2)
        }));
      }
      
      if (data.rarity && !formData.rarity) {
        setFormData(prev => ({ ...prev, rarity: data.rarity }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.type || !formData.country) {
      setError('Type and Country are required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataObj.append(key, formData[key]);
        }
      });

      if (imageFrontFile) {
        formDataObj.append('imageFront', imageFrontFile);
      }
      if (imageBackFile) {
        formDataObj.append('imageBack', imageBackFile);
      }

      if (item) {
        await updateItem(item.id, formDataObj);
      } else {
        await createItem(formDataObj);
      }

      onClose(true);
    } catch (err) {
      setError(err.error || 'Failed to save item');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`max-w-4xl mx-auto p-0 md:p-6`}>
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-xl font-bold uppercase tracking-widest ${themeClasses.textMain}`}>
          {item ? 'Modifica Entry' : 'Nuova Entry'}
        </h2>
        <button onClick={() => onClose(false)} className={`${themeClasses.textMuted} hover:${themeClasses.textMain} transition-colors`}>
          <X size={24} />
        </button>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg flex items-center gap-2">
          <X size={18} /> {error}
        </div>
      )}
      
      {valueInfo && (
        <div className={`mb-6 p-6 rounded-lg border border-yellow-500/20 bg-yellow-500/5 ${themeClasses.textMain}`}>
          <h3 className="font-bold text-yellow-500 mb-2">Value Information</h3>
          <p className="text-sm opacity-90">{valueInfo.note}</p>
          {valueInfo.estimated_value && (
            <p className="font-mono mt-2">Estimated: €{valueInfo.estimated_value.min} - €{valueInfo.estimated_value.max}</p>
          )}
        </div>
      )}

      {/* Numista Search Modal */}
      {showNumistaModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
              <div className={`${themeClasses.card} w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[85vh]`}>
                  <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                    <h3 className={`text-lg font-bold uppercase tracking-wider ${themeClasses.textMain}`}>Search Numista</h3>
                    <button onClick={() => setShowNumistaModal(false)} className={themeClasses.textMuted}><X size={20} /></button>
                  </div>
                  
                  <div className="p-4 border-b border-slate-700/50 flex gap-2">
                      <div className="relative flex-1">
                        <input 
                            type="text" 
                            value={numistaQuery} 
                            onChange={e => setNumistaQuery(e.target.value)} 
                            placeholder="Type to search coin (e.g. 1 Euro Italy 2002)..." 
                            className={`w-full pl-10 pr-4 py-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:ring-2 focus:ring-teal-500 focus:outline-none`}
                            onKeyDown={e => e.key === 'Enter' && handleNumistaSearch()}
                            autoFocus
                        />
                        <Search size={18} className={`absolute left-3 top-3.5 ${themeClasses.textMuted}`} />
                      </div>
                      <button 
                        onClick={handleNumistaSearch} 
                        disabled={searchingNumista}
                        className={`${themeClasses.accentBg} text-white px-6 rounded-lg font-bold shadow-lg shadow-teal-500/20`}
                      >
                          {searchingNumista ? '...' : 'Search'}
                      </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {numistaResults.map(coin => (
                          <div 
                            key={coin.id} 
                            onClick={() => selectNumistaCoin(coin)}
                            className={`p-3 rounded-lg flex gap-4 cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'} border border-transparent hover:border-slate-500/20`}
                          >
                               <div className="w-16 h-16 shrink-0 bg-slate-800/50 rounded flex items-center justify-center overflow-hidden">
                                   {getNumistaImageSrc(coin) ? (
                                     <img
                                       src={getNumistaImageSrc(coin)}
                                       alt={coin.title || 'Numista coin'}
                                       className="w-full h-full object-cover"
                                       loading="lazy"
                                       referrerPolicy="no-referrer"
                                       onError={() => handleNumistaImageError(coin.id)}
                                     />
                                   ) : <span className="text-[10px] text-slate-300">No image</span>}
                               </div>
                               <div className="flex flex-col justify-center">
                                   <strong className={`${themeClasses.textMain} text-sm`}>{coin.title}</strong>
                                   <span className={`${themeClasses.textMuted} text-xs`}>{coin.country?.name} • {coin.min_year}</span>
                               </div>
                          </div>
                      ))}
                      {numistaResults.length === 0 && !searchingNumista && (
                        <div className={`p-10 text-center ${themeClasses.textMuted}`}>Type a query to search the Numista database...</div>
                      )}
                  </div>
              </div>
          </div>
      )}

      {/* Variety Selection Modal */}
      {showVarietyModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className={`${themeClasses.card} w-full max-w-lg rounded-xl shadow-2xl p-6`}>
            <h3 className={`text-lg font-bold uppercase tracking-wider mb-2 ${themeClasses.textMain}`}>Select Variety</h3>
            <p className={`text-sm ${themeClasses.textMuted} mb-6`}>Select the specific variety/mintage for your coin to track it accurately.</p>

            {loadingVarieties ? (
              <p className="py-8 text-center animate-pulse">Loading varieties...</p>
            ) : (
            <div className="mb-6">
                <label className={`block text-xs font-bold uppercase mb-2 ${themeClasses.textMuted}`}>Variety / Issue</label>
                <select 
                    className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain}`}
                    value={selectedVariety}
                    onChange={(e) => setSelectedVariety(e.target.value)}
                >
                    <option value="">Undetermined (Generic Year)</option>
                    {varieties.map(v => (
                        <option key={v.id} value={v.id}>
                            {v.year} {v.mintage ? `- Mintage: ${v.mintage.toLocaleString()}` : ''} {v.comment ? `(${v.comment})` : ''}
                        </option>
                    ))}
                </select>
            </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-700/50">
               <button 
                 type="button" 
                 className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-200'} ${themeClasses.textMuted}`}
                 onClick={() => setShowVarietyModal(false)}
               >
                 Cancel
               </button>
               <button 
                 type="button" 
                 className={`${themeClasses.accentBg} text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg shadow-teal-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                 onClick={confirmAddToCollection} 
                 disabled={loadingVarieties || addingToNumista}
               >
                   {addingToNumista ? 'Adding...' : 'Confirm Add'}
               </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Main Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
            
            {/* Left Column: Image Uploads */}
            <div className="lg:col-span-4 space-y-4">
               <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Images</label>
               
               {/* Front Image */}
               <div className={`relative aspect-square rounded-xl overflow-hidden border-2 border-dashed ${isDarkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-300 hover:border-slate-400'} group transition-all`}>
                  {imageFrontPreview ? (
                     <>
                        <div
                          onClick={() => document.getElementById('file-front')?.click()}
                          onDoubleClick={() => { setViewerSrc(imageFrontPreview); setViewerOpen(true); }}
                          className="w-full h-full cursor-zoom-in"
                        >
                          <HoverImage src={imageFrontPreview} alt="Front" className="w-full h-full object-contain bg-black/20" />
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 z-10">
                           <button type="button" onClick={() => handleRotate('front')} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm" title="Rotate">
                             <RotateCw size={14} />
                           </button>
                           <button
                             type="button"
                             onClick={() => handleAutoCrop('front')}
                             className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm"
                             title="Auto-crop Front"
                             disabled={loading || autoCropping || !item?.id}
                           >
                             <Sparkles size={14} />
                           </button>
                        </div>
                        <label htmlFor="file-front" className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer pointer-events-none">
                           <span className="text-white font-bold text-sm bg-black/50 px-3 py-1 rounded-full backdrop-blur-md">Change Front</span>
                        </label>
                     </>
                  ) : (
                     <label htmlFor="file-front" className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer">
                        <Upload className={`mb-2 opacity-50 ${themeClasses.textMuted}`} />
                        <span className={`text-xs font-bold uppercase ${themeClasses.textMuted}`}>Add Front</span>
                     </label>
                  )}
                  <input type="file" id="file-front" accept="image/*" onChange={(e) => handleImageChange(e, 'front')} className="hidden" />
               </div>

               {/* Back Image */}
               <div className={`relative aspect-square rounded-xl overflow-hidden border-2 border-dashed ${isDarkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-300 hover:border-slate-400'} group transition-all`}>
                  {imageBackPreview ? (
                     <>
                        <div
                          onClick={() => document.getElementById('file-back')?.click()}
                          onDoubleClick={() => { setViewerSrc(imageBackPreview); setViewerOpen(true); }}
                          className="w-full h-full cursor-zoom-in"
                        >
                          <HoverImage src={imageBackPreview} alt="Back" className="w-full h-full object-contain bg-black/20" />
                        </div>
                        <div className="absolute top-2 right-2 flex gap-1 z-10">
                           <button type="button" onClick={() => handleRotate('back')} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm" title="Rotate">
                             <RotateCw size={14} />
                           </button>
                           <button
                             type="button"
                             onClick={() => handleAutoCrop('back')}
                             className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm"
                             title="Auto-crop Back"
                             disabled={loading || autoCropping || !item?.id}
                           >
                             <Sparkles size={14} />
                           </button>
                        </div>
                        <label htmlFor="file-back" className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer pointer-events-none">
                           <span className="text-white font-bold text-sm bg-black/50 px-3 py-1 rounded-full backdrop-blur-md">Change Back</span>
                        </label>
                     </>
                  ) : (
                     <label htmlFor="file-back" className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer">
                        <Upload className={`mb-2 opacity-50 ${themeClasses.textMuted}`} />
                        <span className={`text-xs font-bold uppercase ${themeClasses.textMuted}`}>Add Back</span>
                     </label>
                  )}
                  <input type="file" id="file-back" accept="image/*" onChange={(e) => handleImageChange(e, 'back')} className="hidden" />
               </div>

                  {viewerOpen && viewerSrc && (
                    <FullscreenImageViewer src={viewerSrc} alt="Full Image" onClose={() => { setViewerOpen(false); setViewerSrc(null); }} />
                  )}

                <button 
                  type="button" 
                  onClick={handleAIIdentify} 
                  disabled={loading}
                  className={`w-full py-3 rounded-lg font-bold uppercase text-xs tracking-wider flex items-center justify-center gap-2 transition-all ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} disabled:opacity-50`}
                >
                    <Sparkles size={16} /> Identify with AI
                </button>

                <button
                  type="button"
                  onClick={() => handleAutoCrop('both')}
                  disabled={loading || autoCropping || !item?.id}
                  className={`w-full py-3 rounded-lg font-bold uppercase text-xs tracking-wider flex items-center justify-center gap-2 transition-all ${isDarkMode ? 'bg-cyan-700 hover:bg-cyan-600 text-white' : 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200'} disabled:opacity-50`}
                  title={item?.id ? 'Auto-crop front and back with vision processing' : 'Save item first to run auto-crop'}
                >
                  <Sparkles size={16} /> {autoCropping ? 'Auto-cropping...' : 'Auto-crop Front & Back'}
                </button>

            </div>

            {/* Right Column: Fields */}
            <div className="lg:col-span-8 space-y-6">

                {/* Country Row */}
                <div className="space-y-2">
                  <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Country & Type</label>
                  <div className="flex gap-4">
                     <select 
                        name="type" 
                        value={formData.type} 
                        onChange={handleChange}
                        className={`w-1/3 p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                     >
                       <option value="coin">Coin</option>
                       <option value="banknote">Banknote</option>
                     </select>
                     
                     <div className="relative flex-1">
                        <input
                           type="text"
                           name="country"
                           value={formData.country}
                           onChange={handleChange}
                           placeholder="Country (e.g. Italy, USA)"
                           className={`w-full p-3 pr-24 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                           required
                        />
                        <button 
                           type="button"
                           onClick={openNumistaSearch}
                           className="absolute right-2 top-2 bottom-2 px-3 text-xs bg-slate-700/50 hover:bg-slate-700 text-slate-300 rounded transition-colors"
                        >
                           Search DB
                        </button>
                     </div>
                  </div>
                </div>

                {/* Year & Denomination */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Year</label>
                       <input
                          type="number"
                          name="year"
                          value={formData.year}
                          onChange={handleChange}
                          placeholder="YYYY"
                          className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                    </div>
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Denomination</label>
                       <input
                          type="text"
                          name="denomination"
                          value={formData.denomination}
                          onChange={handleChange}
                          placeholder="e.g. 1 Euro"
                          className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                    </div>
                </div>

                 {/* Currency & Material */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Currency</label>
                       <input
                          type="text"
                          name="currency"
                          value={formData.currency}
                          onChange={handleChange}
                          placeholder="e.g. EUR"
                          className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                    </div>
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Material</label>
                       <input
                          type="text"
                          name="material"
                          value={formData.material}
                          onChange={handleChange}
                          placeholder="e.g. Gold"
                          className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                    </div>
                </div>
                
                {/* Rarity & Condition */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Rarity</label>
                      <select 
                        name="rarity" 
                        value={formData.rarity} 
                        onChange={handleChange}
                        className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                      >
                        <option value="">Select rarity</option>
                        <option value="common">Common</option>
                        <option value="uncommon">Uncommon</option>
                        <option value="rare">Rare</option>
                        <option value="very_rare">Very Rare</option>
                        <option value="extremely_rare">Extremely Rare</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Condition</label>
                      <select 
                        name="condition" 
                        value={formData.condition} 
                        onChange={handleChange}
                        className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                      >
                        <option value="">Select condition</option>
                        <option value="poor">Poor</option>
                        <option value="fair">Fair</option>
                        <option value="good">Good</option>
                        <option value="very_good">Very Good</option>
                        <option value="excellent">Excellent</option>
                        <option value="uncirculated">Uncirculated</option>
                      </select>
                    </div>
                </div>

                {/* Value & Quantity */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Estimated Value (€)</label>
                       <input
                          type="number"
                          name="estimated_value"
                          value={formData.estimated_value}
                          onChange={handleChange}
                          placeholder="0.00"
                          step="0.01"
                          className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                    </div>
                    <div className="space-y-2">
                       <label className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Quantity</label>
                       <input
                         type="number"
                         name="quantity"
                         value={formData.quantity}
                         onChange={handleChange}
                         placeholder="1"
                         min="1"
                         className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500`}
                       />
                    </div>
                </div>

                {/* Numista Status Bar */}
                <div className={`${themeClasses.card} p-3 rounded-lg border border-dashed border-slate-700/50 flex flex-col sm:flex-row gap-4 items-center justify-between`}>
                    <div className="flex items-center gap-3">
                        <span className="text-xl">📚</span>
                        <div>
                           <p className={`text-sm font-bold ${themeClasses.textMain} uppercase tracking-wider`}>Numista Database</p>
                          {numistaId ? 
                            <p className="text-xs text-green-500 flex items-center gap-1"><Check size={12} /> Linked to Numista (ID: {numistaId})</p> : 
                            <p className={`text-xs ${themeClasses.textMuted}`}>No Numista coin selected yet</p>
                           }
                        </div>
                    </div>
                    <div>
                         {numistaId ? (
                            <div className="flex gap-2">
                                <a 
                                    href={`https://en.numista.com/catalogue/pieces${numistaId}.html`} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className={`px-3 py-1.5 text-xs rounded border ${isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-slate-300 hover:bg-slate-100 text-slate-600'} flex items-center gap-1 transition-colors`}
                                >
                                    View <ExternalLink size={10} />
                                </a>
                                {(numistaCollectionId || item?.numista_collection_id) ? (
                                    <button
                                        type="button"
                                        onClick={async () => { if (window.confirm("Update Numista variety/details?")) fetchVarietiesAndOpenModal(); }}
                                        className="px-3 py-1.5 text-xs rounded bg-green-500/10 text-green-500 border border-green-500/30 flex items-center gap-1 font-bold"
                                    >
                                        In Collection
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={fetchVarietiesAndOpenModal}
                                        className="px-3 py-1.5 text-xs rounded bg-sky-500/10 text-sky-500 border border-sky-500/30 flex items-center gap-1 font-bold hover:bg-sky-500/20 transition-colors"
                                    >
                                        + Add to My Collection
                                    </button>
                                )}
                            </div>
                        ) : (
                            <span className={`text-xs ${themeClasses.textMuted} italic`}>Use Search DB and select a coin to link this item</span>
                        )}
                    </div>
                </div>

                {/* Text Areas */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="description" className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Details about the coin..."
                      rows="3"
                      className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500 min-h-[80px]`}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="notes" className={`block text-xs font-bold uppercase ${themeClasses.textMuted}`}>Private Notes</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Provenance, purchase price, storage location..."
                      rows="3"
                      className={`w-full p-3 rounded-lg ${themeClasses.input} ${themeClasses.textMain} focus:outline-none focus:ring-1 focus:ring-teal-500 min-h-[80px]`}
                    />
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex gap-4 pt-4 border-t border-slate-700/50 justify-end">
                    <button 
                        type="button" 
                        onClick={() => onClose(false)} 
                        className={`px-6 py-2 rounded-lg font-bold transition-all ${isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-200 text-slate-600'}`}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className={`${themeClasses.accentBg} text-white px-8 py-2 rounded-lg font-bold shadow-lg shadow-teal-500/20 hover:opacity-90 transition-opacity disabled:opacity-50`}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : (item ? 'Update Entry' : 'Create Entry')}
                    </button>
                </div>

            </div>
        </div>
      </form>
    </div>
  );
};

export default ItemForm;
